import { useCallback, useRef } from 'react';
import { canUseDOM, getWindow } from '@surfline/web-common';
import type { Map } from 'leaflet';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';

import config from 'config';
import type { Location as MapLocation } from 'types/map';

interface MapOptions {
  maxZoom: number;
  minZoom: number;
}

interface UseMapInitializationProps {
  mapContainerRef: React.RefObject<HTMLDivElement>;
  location: MapLocation;
  mapOptions: MapOptions;
  onMoveStartRef: React.MutableRefObject<(() => void) | null>;
  onMoveEndRef: React.MutableRefObject<(() => void) | null>;
  updateSpots: () => void;
  leafletMapRef?: React.MutableRefObject<Map | null>; // Update to allow null
  LRef?: React.MutableRefObject<typeof import('leaflet') | null>; // Update to allow null
}

/**
 * Hook to handle Leaflet map initialization
 */
export const useMapInitialization = ({
  mapContainerRef,
  location,
  mapOptions,
  onMoveStartRef,
  onMoveEndRef,
  updateSpots,
  leafletMapRef: externalLeafletMapRef,
  LRef: externalLRef,
}: UseMapInitializationProps) => {
  // Use provided refs or create new ones with proper null assertion
  const internalLeafletMapRef = useRef<Map | null>(null);
  const internalLRef = useRef<typeof import('leaflet') | null>(null);

  // Use external refs if provided, otherwise use internal ones
  const leafletMapRef = externalLeafletMapRef || internalLeafletMapRef;
  const LRef = externalLRef || internalLRef;

  const isMapInitializedRef = useRef(false);
  const spotsUpdateScheduledRef = useRef(false);

  // Initialize Leaflet map
  const initializeMap = useCallback(() => {
    if (!canUseDOM || !mapContainerRef.current) return;

    // Dynamically load Leaflet if needed
    const win = getWindow();
    if (!LRef.current && !/HeadlessChrome/.test(win.navigator.userAgent)) {
      LRef.current = require('leaflet');
    }

    if (!LRef.current) return;

    // If map is already initialized, just update spots
    if (isMapInitializedRef.current && leafletMapRef.current) {
      if (!spotsUpdateScheduledRef.current) {
        spotsUpdateScheduledRef.current = true;
        updateSpots();
      }
      return;
    }

    try {
      // Clean up any existing map
      if (leafletMapRef.current) {
        try {
          if (onMoveEndRef.current) {
            leafletMapRef.current.off('moveend', onMoveEndRef.current);
          }
          if (onMoveStartRef.current) {
            leafletMapRef.current.off('movestart', onMoveStartRef.current);
          }
          leafletMapRef.current.remove();
        } catch (error) {
          reportErrorToNewRelic(error as Error, 'useMapInitialization', {
            source: 'useMapInitialization',
            context: 'cleanup existing map',
          });
          console.error('Map cleanup error:', error);
        }
      }

      // Create new map instance
      leafletMapRef.current = LRef.current.map('rasterMap', {
        center: [
          location.center.lat !== undefined && location.center.lat !== null
            ? location.center.lat
            : 33.5,
          location.center.lon !== undefined && location.center.lon !== null
            ? location.center.lon
            : -117.7,
        ],
        zoom: location.zoom !== undefined && location.zoom !== null ? location.zoom : 8,
        maxZoom: mapOptions.maxZoom,
        minZoom: mapOptions.minZoom,
        preferCanvas: true,
        worldCopyJump: true,
        renderer: LRef.current.canvas(),
        zoomAnimation: false, // Disable animations to reduce re-renders
        fadeAnimation: false, // Disable animations to reduce re-renders
        markerZoomAnimation: false, // Disable animations to reduce re-renders
      });

      // Add event listeners using refs (but only if they contain functions)
      if (onMoveEndRef.current) {
        leafletMapRef.current.on('moveend', onMoveEndRef.current);
      }
      if (onMoveStartRef.current) {
        leafletMapRef.current.on('movestart', onMoveStartRef.current);
      }

      // Add tile layer
      LRef.current
        .tileLayer(`${config.mapTileUrl}/256/{z}/{x}/{y}.png?key=${config.mapTileKey}`, {
          detectRetina: true,
          maxZoom: mapOptions.maxZoom,
          minZoom: mapOptions.minZoom,
          tileSize: 256,
        })
        .addTo(leafletMapRef.current);

      // Add attribution
      LRef.current.control
        .attribution({ prefix: false })
        .addAttribution(
          '<a href="https://www.maptiler.com/copyright" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap</a> contributors',
        )
        .addTo(leafletMapRef.current);

      // Mark as initialized
      isMapInitializedRef.current = true;

      // Schedule spots update
      spotsUpdateScheduledRef.current = true;
      updateSpots();
    } catch (error) {
      reportErrorToNewRelic(error as Error, 'useMapInitialization', {
        source: 'useMapInitialization',
        context: 'map initialization',
        location: JSON.stringify(location),
        mapOptions: JSON.stringify(mapOptions),
      });
      console.error('Map initialization error:', error);
    }
  }, [
    location,
    mapOptions,
    updateSpots,
    onMoveEndRef,
    onMoveStartRef,
    mapContainerRef,
    leafletMapRef,
    LRef,
  ]);

  // Add a function to bind/rebind event handlers
  const bindEventHandlers = useCallback(() => {
    if (!leafletMapRef.current) return;

    try {
      // Unbind previous handlers
      leafletMapRef.current.off('moveend');
      leafletMapRef.current.off('movestart');

      // Add new ones if available
      if (onMoveEndRef.current) {
        leafletMapRef.current.on('moveend', onMoveEndRef.current);
      }
      if (onMoveStartRef.current) {
        leafletMapRef.current.on('movestart', onMoveStartRef.current);
      }
    } catch (error) {
      reportErrorToNewRelic(error as Error, 'useMapInitialization', {
        source: 'useMapInitialization',
        context: 'bindEventHandlers',
      });
      console.error('Event handler binding error:', error);
    }
  }, [onMoveEndRef, onMoveStartRef, leafletMapRef]);

  return {
    leafletMapRef,
    LRef,
    isMapInitializedRef,
    spotsUpdateScheduledRef,
    initializeMap,
    bindEventHandlers,
  };
};

export default useMapInitialization;
